import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
  GridColDefExtended,
  GridFilterModel,
  gridRowSelectionIdsSelector,
  GridSlots,
  GridSortModel,
  useGridApiRef
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { useAppSelector } from '@reduxStore';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { getDispatchGridColDefs } from './columns';
import { getDispatchCardQuickFilterValue } from '@dispatchBoard/getDispatchCardQuickFilter';
import DispatchCard from '@components/DataGrid/Dispatch/Cells/DispatchCard';
import BrandLinearProgress from '@/Infrastructure/Components/Loading/BrandLinearProgress';
import DispatchGridToolbar from './DispatchGridToolbar';
import { convertQuerySortOrderToGridSortModel, defaultFilterModel } from './types';
import useDispatchRows from '@hooks/useDispatchRows';
import useDispatchBoardQueries from '@hooks/useDispatchBoardQueries';
import { applyThemeGridSxOverrides } from '@/Infrastructure/Theme/AppThemeOverrides';
import { defaultAllOpenQuery } from '@api/Trinium/Endpoints/BoardQueries/types';
import { useNavigate, useSearchParams } from 'react-router';
import { GenericOverlay } from '../GenericNoRowsOverlay';
import { emptyRowSet } from '../types';
import EditableCellPopover, { EditableCellPopoverRef } from '../EditableCellPopover';

import styles from '../RenderCell.module.css';

const ROW_CLASS_NAME = styles['custom-row'];
const getRowId = (row: DispatchLeg) => row.legId;
const getRowClassName = () => ROW_CLASS_NAME;

/**
 * Dispatch Grid component implementing the MUI DataGridPro on Mobile devices
 */
export default function MobileGrid() {
  const popoverRef = useRef<EditableCellPopoverRef>(null);
  const [, startTransition] = useTransition();

  const navigate = useNavigate();
  const [searchParameters] = useSearchParams();
  const isJobMode = !!searchParameters.get('job');
  const { selectedBoardQueryName, selectedTerminal, quickFilterLogicOperator } = useAppSelector((state) => state.applicationState);
  const { defaultQuery, currentQuery } = useDispatchBoardQueries(selectedTerminal?.number, selectedBoardQueryName);
  const { dispatchLegs, mode, isLoading } = useDispatchRows(currentQuery);

  /** Data Grid API ref for accessing Grid outside the render */
  const gridApiRef = useGridApiRef();

  /** We want to use a controlled state for the selection model to allow the toolbar to see row selection changes */
  const [rowSelectionModel, setRowSelectionModel] = useState(emptyRowSet);

  /** Tracking Data Grid filtering */
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    ...defaultFilterModel,
    quickFilterLogicOperator,
    quickFilterValues: []
  });

  /** Sync quick filter logic operator from Redux into the filter model */
  useEffect(() => {
    setFilterModel((prev) => ({
      ...prev,
      quickFilterLogicOperator
    }));
  }, [quickFilterLogicOperator]);

  /** Using manual sorting model when the user is filtering */
  const [sortingModel, setSortingModel] = useState<GridSortModel>();

  /** Update sorting model when current query changes. In job/consignment view (e.g. "View All Legs for this Container"),
   * clear the sort so the data layer's csn/leg-number ordering from sortJobLegs takes effect rather than retaining the parent query's sort. */
  useEffect(() => {
    if (isJobMode) {
      setSortingModel([]);
    } else if (currentQuery.configuration.sortOrder?.length) {
      const sortModel = convertQuerySortOrderToGridSortModel(currentQuery);
      setSortingModel(sortModel);
    } else {
      setSortingModel([]);
    }
  }, [currentQuery, isJobMode]);

  /**
   * Override for the Filter Model change event to allow us to clear the selected rows when the filter/dataset changes.
   * @param gridFilterModel MUI GridFilterModel
   */
  const changeFilterModel = (gridFilterModel: GridFilterModel) => {
    gridApiRef.current?.setRowSelectionModel(emptyRowSet);

    startTransition(() => {
      /** When the user is typing in a filter and they are not on the default board query,
       * return them to the default query so that they are filtering against all legs.
       */
      if ((gridFilterModel.quickFilterValues?.length ?? 0) > 0) {
        void navigate(`/${String(selectedTerminal?.number)}/Board/${defaultQuery ? defaultAllOpenQuery.name : ''}`);
        setSortingModel([{ field: 'orderNumber', sort: 'asc' }]);
      } else {
        setSortingModel([]);
      }

      setFilterModel(gridFilterModel);
    });
  };

  const changeSortModel = useCallback((gridSortModel: GridSortModel) => {
    setSortingModel(gridSortModel);
  }, []);

  /** Retrieve the Data Grid columns for the current Board Query */
  const gridColumns = useMemo(() => {
    return getDispatchGridColDefs(currentQuery);
  }, [currentQuery]);

  /** Group that is currently viewable on mobile card */
  const [activeGroup, setActiveGroup] = useState(0);

  const onMobileCellClickHandler = useCallback(
    (target: EventTarget & HTMLElement, columnDetails: GridColDefExtended<DispatchLeg>, row: DispatchLeg) => {
      if (popoverRef.current && columnDetails.renderEditableCell) {
        let selectedRows = [row];

        if (columnDetails.disableBatchEdit !== true) {
          /** Editing a cell selects its row so the clicked leg is always part of a batch save */
          gridApiRef.current?.selectRow(row.legId, true);
          selectedRows = Array.from(gridRowSelectionIdsSelector(gridApiRef).values()) as DispatchLeg[];

          if (typeof columnDetails.disableBatchEdit === 'function' && columnDetails.disableBatchEdit(selectedRows)) {
            selectedRows = [row];
          }
        }

        const editableContent = columnDetails.renderEditableCell(row, popoverRef.current.editableRef, selectedRows);
        if (editableContent) {
          popoverRef.current.open(target, editableContent, selectedRows.length);
        }
      }
    },
    [gridApiRef]
  );

  const columns: GridColDef[] = [
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF
    },
    {
      field: 'Mobile Card',
      type: 'actions',
      flex: 1,
      renderCell: ({ row }: { row: DispatchLeg }) => (
        <DispatchCard
          key={
            mode === 'dispatch'
              ? row.legId
              : mode === 'consignment'
                ? `c${String(row.legId)}`
                : `j${String(row.legId)}-${String(activeGroup)}`
          }
          leg={row}
          gridColumnDetails={gridColumns}
          gridPinnedColumns={currentQuery.configuration.pinnedCols}
          activeGroup={activeGroup}
          setActiveGroup={setActiveGroup}
          renderEditable={onMobileCellClickHandler}
        />
      ),
      getApplyQuickFilterFn: (value: string) => getDispatchCardQuickFilterValue(value, gridColumns)
    }
  ];

  /** Memoized slots to prevent re-renders */
  const slots = useMemo<Partial<GridSlots>>(
    () => ({
      loadingOverlay: BrandLinearProgress,
      noRowsOverlay: () => <GenericOverlay overlayText='No Dispatch Legs Found.' />,
      noResultsOverlay: () => <GenericOverlay overlayText='No Dispatch Leg Results.' />,
      toolbar: DispatchGridToolbar
    }),
    []
  );

  /** Memoized row selection change handler */
  const onRowSelectionModelChange = useCallback((model: typeof rowSelectionModel) => {
    setRowSelectionModel(model);
  }, []);

  return (
    <>
      <DataGridPro
        apiRef={gridApiRef}
        rows={dispatchLegs}
        rowHeight={200}        
        columns={columns}
        columnHeaderHeight={0}
        getRowId={getRowId}
        getRowClassName={getRowClassName}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        filterModel={filterModel}
        onFilterModelChange={changeFilterModel}
        sortModel={sortingModel}
        onSortModelChange={changeSortModel}
        loading={isLoading}
        slots={slots}
        sx={{
          ...applyThemeGridSxOverrides(),
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: 'none'
          },
          '& .MuiDataGrid-cell': {
            border: 'none'
          },
          '& .MuiDataGrid-row.Mui-hovered': {
            backgroundColor: 'inherit'
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'inherit'
          },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'transparent'
          },
          '& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
            backgroundColor: 'transparent'
          },
          '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: 'transparent'
          }
        }}
        columnVisibilityModel={{
          __check__: false
        }}
        disableRowSelectionOnClick
      />
      <EditableCellPopover ref={popoverRef} />
    </>
  );
}
